/**
 * <bfg-gallery> — Bundles + Free Gift block-driven gallery.
 *
 * Scrolling is the source of truth: arrows and thumbnails scroll the track, and
 * the active index is derived back from scroll position. That keeps native swipe,
 * keyboard scrolling and JS navigation in agreement without a separate state
 * variable that can drift.
 *
 * Two instances live on a gender-switching page (one per gender). The inactive
 * one is `display: none`, where every offsetWidth reads 0 — so measurements are
 * taken lazily at interaction time, never cached at connect.
 */
(function () {
  'use strict';

  if (customElements.get('bfg-gallery')) return;

  customElements.define(
    'bfg-gallery',
    class BfgGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-bfg-track]');
        if (!this.track) return;

        this.slides = Array.from(this.querySelectorAll('[data-bfg-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-bfg-thumb]'));
        this.prevBtn = this.querySelector('[data-bfg-prev]');
        this.nextBtn = this.querySelector('[data-bfg-next]');
        this.index = 0;

        this._onScroll = this._onScroll.bind(this);
        this._onThumbClick = this._onThumbClick.bind(this);

        this.track.addEventListener('scroll', this._onScroll, { passive: true });
        this.prevBtn?.addEventListener('click', () => this.go(this.index - 1));
        this.nextBtn?.addEventListener('click', () => this.go(this.index + 1));
        this.thumbs.forEach((thumb) => thumb.addEventListener('click', this._onThumbClick));

        this._syncControls();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this._onScroll);
        this.thumbs?.forEach((thumb) => thumb.removeEventListener('click', this._onThumbClick));
      }

      _onThumbClick(event) {
        const index = Number(event.currentTarget.dataset.bfgThumb);
        if (Number.isFinite(index)) this.go(index);
      }

      /**
       * Scroll position → index. rAF-throttled because scroll fires far more often
       * than the ~1 index change it can produce.
       */
      _onScroll() {
        if (this._scrollFrame) return;
        this._scrollFrame = requestAnimationFrame(() => {
          this._scrollFrame = null;
          const next = this._indexFromScroll();
          if (next === this.index) return;
          this.index = next;
          this._syncControls();
        });
      }

      _indexFromScroll() {
        const width = this.track.clientWidth;
        if (!width) return this.index;
        const raw = Math.round(this.track.scrollLeft / width);
        return Math.min(Math.max(raw, 0), this.slides.length - 1);
      }

      go(index) {
        const clamped = Math.min(Math.max(index, 0), this.slides.length - 1);
        const slide = this.slides[clamped];
        if (!slide) return;

        // scrollIntoView would also scroll the page when the gallery is partly
        // offscreen; setting scrollLeft only moves the track.
        this.track.scrollLeft = slide.offsetLeft - this.track.offsetLeft;
        this.index = clamped;
        this._syncControls();
      }

      _syncControls() {
        this.thumbs.forEach((thumb) => {
          const isActive = Number(thumb.dataset.bfgThumb) === this.index;
          if (isActive) {
            thumb.setAttribute('aria-current', 'true');
            thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          } else {
            thumb.removeAttribute('aria-current');
          }
        });

        if (this.prevBtn) this.prevBtn.hidden = this.index <= 0;
        if (this.nextBtn) this.nextBtn.hidden = this.index >= this.slides.length - 1;
      }
    }
  );
})();
